// ==========================================================
// app/regles-communautaires.tsx — Règles Communautaires v1.2
// ==========================================================

import React from 'react';
import { useTranslation } from 'react-i18next';
import LegalDocumentScreen, { LegalSection } from '../components/LegalDocumentScreen';

const VIOLET       = '#6411A9';
const VIOLET_LIGHT = '#9B30E8';
const GOLD         = '#F5A623';
const SUCCESS      = '#00A86B';
const DANGER       = '#E8003D';
const BLUE         = '#3B82F6';

function useSections(t: (key: string, opts?: any) => any): LegalSection[] {
  return [
    {
      id: 'respect', num: '01', icon: 'heart-outline', color: VIOLET_LIGHT,
      title: t('reglesCommunautaires.sections.respect.title'),
      content: [
        { type: 'paragraph', text: t('reglesCommunautaires.sections.respect.p1') },
        { type: 'subtitle', text: t('reglesCommunautaires.sections.respect.sub1') },
        { type: 'bullet', items: t('reglesCommunautaires.sections.respect.bullets1', { returnObjects: true }) },
      ],
    },
    {
      id: 'contenu-interdit', num: '02', icon: 'ban-outline', color: DANGER,
      title: t('reglesCommunautaires.sections.contenuInterdit.title'),
      content: [
        { type: 'paragraph', text: t('reglesCommunautaires.sections.contenuInterdit.p1') },
        { type: 'subtitle', text: t('reglesCommunautaires.sections.contenuInterdit.sub1') },
        { type: 'paragraph', text: t('reglesCommunautaires.sections.contenuInterdit.p2') },
        { type: 'subtitle', text: t('reglesCommunautaires.sections.contenuInterdit.sub2') },
        { type: 'paragraph', text: t('reglesCommunautaires.sections.contenuInterdit.p3') },
        { type: 'subtitle', text: t('reglesCommunautaires.sections.contenuInterdit.sub3') },
        { type: 'paragraph', text: t('reglesCommunautaires.sections.contenuInterdit.p4') },
        { type: 'subtitle', text: t('reglesCommunautaires.sections.contenuInterdit.sub4') },
        { type: 'paragraph', text: t('reglesCommunautaires.sections.contenuInterdit.p5') },
        { type: 'subtitle', text: t('reglesCommunautaires.sections.contenuInterdit.sub5') },
        { type: 'bullet', items: t('reglesCommunautaires.sections.contenuInterdit.bullets1', { returnObjects: true }) },
      ],
    },
    {
      id: 'publications', num: '03', icon: 'create-outline', color: VIOLET,
      title: t('reglesCommunautaires.sections.publications.title'),
      content: [
        { type: 'subtitle', text: t('reglesCommunautaires.sections.publications.sub1') },
        { type: 'bullet', items: t('reglesCommunautaires.sections.publications.bullets1', { returnObjects: true }) },
        { type: 'highlight', text: t('reglesCommunautaires.sections.publications.highlight1'), icon: 'checkmark-circle' },
        { type: 'subtitle', text: t('reglesCommunautaires.sections.publications.sub2') },
        { type: 'paragraph', text: t('reglesCommunautaires.sections.publications.p1') },
        { type: 'subtitle', text: t('reglesCommunautaires.sections.publications.sub3') },
        { type: 'paragraph', text: t('reglesCommunautaires.sections.publications.p2') },
      ],
    },
    {
      id: 'messagerie', num: '04', icon: 'chatbubbles-outline', color: BLUE,
      title: t('reglesCommunautaires.sections.messagerie.title'),
      content: [
        { type: 'paragraph', text: t('reglesCommunautaires.sections.messagerie.p1') },
        { type: 'highlight', text: t('reglesCommunautaires.sections.messagerie.highlight1'), icon: 'ban' },
      ],
    },
    {
      id: 'concours', num: '05', icon: 'trophy-outline', color: GOLD,
      title: t('reglesCommunautaires.sections.concours.title'),
      content: [
        { type: 'subtitle', text: t('reglesCommunautaires.sections.concours.sub1') },
        { type: 'paragraph', text: t('reglesCommunautaires.sections.concours.p1') },
        { type: 'subtitle', text: t('reglesCommunautaires.sections.concours.sub2') },
        { type: 'bullet', items: t('reglesCommunautaires.sections.concours.bullets1', { returnObjects: true }) },
        { type: 'highlight', text: t('reglesCommunautaires.sections.concours.highlight1'), icon: 'shield-checkmark' },
        { type: 'subtitle', text: t('reglesCommunautaires.sections.concours.sub3') },
        { type: 'paragraph', text: t('reglesCommunautaires.sections.concours.p2') },
        { type: 'subtitle', text: t('reglesCommunautaires.sections.concours.sub4') },
        { type: 'paragraph', text: t('reglesCommunautaires.sections.concours.p3') },
        { type: 'subtitle', text: t('reglesCommunautaires.sections.concours.sub5') },
        { type: 'paragraph', text: t('reglesCommunautaires.sections.concours.p4') },
      ],
    },
    {
      id: 'publicite', num: '06', icon: 'megaphone-outline', color: '#E67E22',
      title: t('reglesCommunautaires.sections.publicite.title'),
      content: [
        { type: 'paragraph', text: t('reglesCommunautaires.sections.publicite.p1') },
        { type: 'highlight', text: t('reglesCommunautaires.sections.publicite.highlight1'), icon: 'ban' },
      ],
    },
    {
      id: 'boutiques', num: '07', icon: 'storefront-outline', color: VIOLET,
      title: t('reglesCommunautaires.sections.boutiques.title'),
      content: [
        { type: 'paragraph', text: t('reglesCommunautaires.sections.boutiques.p1') },
        { type: 'highlight', text: t('reglesCommunautaires.sections.boutiques.highlight1'), icon: 'shield-checkmark' },
        { type: 'subtitle', text: t('reglesCommunautaires.sections.boutiques.sub1') },
        { type: 'bullet', items: t('reglesCommunautaires.sections.boutiques.bullets1', { returnObjects: true }) },
        { type: 'paragraph', text: t('reglesCommunautaires.sections.boutiques.p2') },
      ],
    },
    {
      id: 'signalement', num: '08', icon: 'flag-outline', color: VIOLET_LIGHT,
      title: t('reglesCommunautaires.sections.signalement.title'),
      content: [
        { type: 'subtitle', text: t('reglesCommunautaires.sections.signalement.sub1') },
        { type: 'paragraph', text: t('reglesCommunautaires.sections.signalement.p1') },
        { type: 'subtitle', text: t('reglesCommunautaires.sections.signalement.sub2') },
        { type: 'bullet', items: t('reglesCommunautaires.sections.signalement.bullets1', { returnObjects: true }) },
        { type: 'highlight', text: t('reglesCommunautaires.sections.signalement.highlight1'), icon: 'warning' },
      ],
    },
    {
      id: 'sanctions', num: '09', icon: 'alert-circle-outline', color: DANGER,
      title: t('reglesCommunautaires.sections.sanctions.title'),
      content: [
        { type: 'subtitle', text: t('reglesCommunautaires.sections.sanctions.sub1') },
        { type: 'paragraph', text: t('reglesCommunautaires.sections.sanctions.p1') },
        { type: 'subtitle', text: t('reglesCommunautaires.sections.sanctions.sub2') },
        { type: 'paragraph', text: t('reglesCommunautaires.sections.sanctions.p2') },
        { type: 'subtitle', text: t('reglesCommunautaires.sections.sanctions.sub3') },
        { type: 'highlight', text: t('reglesCommunautaires.sections.sanctions.highlight1'), icon: 'alert-circle' },
      ],
    },
    {
      id: 'api', num: '10', icon: 'code-slash-outline', color: DANGER,
      title: t('reglesCommunautaires.sections.api.title'),
      content: [
        { type: 'paragraph', text: t('reglesCommunautaires.sections.api.p1') },
        { type: 'bullet', items: t('reglesCommunautaires.sections.api.bullets1', { returnObjects: true }) },
        { type: 'highlight', text: t('reglesCommunautaires.sections.api.highlight1'), icon: 'alert-circle' },
        { type: 'paragraph', text: t('reglesCommunautaires.sections.api.p2') },
      ],
    },
    {
      id: 'evolution', num: '11', icon: 'refresh-outline', color: BLUE,
      title: t('reglesCommunautaires.sections.evolution.title'),
      content: [
        { type: 'paragraph', text: t('reglesCommunautaires.sections.evolution.p1') },
        { type: 'highlight', text: t('reglesCommunautaires.sections.evolution.highlight1'), icon: 'checkmark-circle' },
      ],
    },
    {
      id: 'contact', num: '12', icon: 'mail-outline', color: SUCCESS,
      title: t('reglesCommunautaires.sections.contact.title'),
      content: [
        { type: 'highlight', text: 'harmonia@harmoniaworld.world', icon: 'mail' },
        { type: 'paragraph', text: t('reglesCommunautaires.sections.contact.p1') },
      ],
    },
  ];
}

export default function ReglesCommunautairesScreen() {
  const { t } = useTranslation();
  const SECTIONS = useSections(t);

  return (
    <LegalDocumentScreen
      title={t('reglesCommunautaires.title')}
      version="v1.2"
      sections={SECTIONS}
      currentRoute="/regles-communautaires"
      stats={[
        { value: '12', label: t('reglesCommunautaires.stats.sections') },
        { isIcon: true, iconName: 'people',       iconColor: '#FFC947', label: t('reglesCommunautaires.stats.forEveryone') },
        { isIcon: true, iconName: 'shield-checkmark', iconColor: '#FFC947', label: t('reglesCommunautaires.stats.healthyCommunity') },
      ]}
    />
  );
}
